import { useState, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Navbar, Footer } from "@/components/Layout";
import { ListingCard } from "@/components/ListingCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import MarketplaceFiltersSidebar from "@/components/MarketplaceFiltersSidebar";
import { ListingCardSkeleton } from "@/components/ListingCardSkeleton";
import { defaultFilters, filterProvinces, type MarketplaceFilters } from "@/lib/filterData";
import { useSeo } from "@/hooks/useSeo";

const FILTERS_STORAGE_KEY = "marketplace-filters-v1";

export default function Marketplace() {
  useSeo({
    title: "Annonser — virke, ved och skogsprodukter",
    description: "Bläddra bland aktuella annonser på virke, ved, plankor, stockar och skogsprodukter från säljare i hela Sverige.",
    path: "/annonser",
  });
  const location = useLocation();
  const [filters, setFilters] = useState<MarketplaceFilters>(() => {
    if ((location.state as any)?.fresh) {
      sessionStorage.removeItem(FILTERS_STORAGE_KEY);
      return { ...defaultFilters };
    }
    try {
      const saved = sessionStorage.getItem(FILTERS_STORAGE_KEY);
      if (saved) return { ...defaultFilters, ...JSON.parse(saved) };
    } catch {
      // ignore
    }
    return { ...defaultFilters };
  });
  useEffect(() => {
    try {
      sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch {
      // ignore
    }
  }, [filters]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useIsMobile();

  // fetch listings from DB
  const { data: listings = [], isLoading } = useQuery({
    queryKey: ["marketplace-listings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("listings")
        .select("*")
        .eq("status", "aktiv")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  // client-side filtering
  const filtered = useMemo(() => {
    return listings.filter((l) => {
      // search
      if (filters.search) {
        const q = filters.search.toLowerCase();
        if (
          !l.title.toLowerCase().includes(q) &&
          !(l.location ?? "").toLowerCase().includes(q) &&
          !(l.description ?? "").toLowerCase().includes(q)
        )
          return false;
      }

      // categories (OR)
      if (filters.categories.length > 0) {
        if (!filters.categories.includes(l.category)) return false;
      }

      // species (OR) — case-insensitive compare
      if (filters.species.length > 0) {
        const speciesLower = filters.species.map((s) => s.toLowerCase());
        if (!speciesLower.includes(l.species.toLowerCase())) return false;
      }

      // moisture (OR)
      if (filters.moisture.length > 0 && l.moisture_content) {
        if (!filters.moisture.includes(l.moisture_content)) return false;
      } else if (filters.moisture.length > 0 && !l.moisture_content) {
        return false;
      }

      // location: municipality or province match
      if (filters.municipalities.length > 0 || filters.provinces.length > 0) {
        const loc = (l.municipality ?? l.location ?? "").toLowerCase();

        // Build set of allowed municipalities
        const allowedMunicipalities = new Set<string>();

        // Add explicitly selected municipalities
        filters.municipalities.forEach((m) => allowedMunicipalities.add(m.toLowerCase()));

        // For each selected province, if no specific kommun within it is selected,
        // expand to all kommuner in that province
        filters.provinces.forEach((prov) => {
          const provinceData = filterProvinces.find((fp) => fp.name === prov);
          if (!provinceData) return;
          const hasSpecificKommun = provinceData.municipalities.some((m) =>
            filters.municipalities.includes(m),
          );
          if (!hasSpecificKommun) {
            provinceData.municipalities.forEach((m) =>
              allowedMunicipalities.add(m.toLowerCase()),
            );
          }
        });

        if (!allowedMunicipalities.has(loc)) return false;
      }

      // price range
      if (l.price < filters.priceRange[0]) return false;
      if (filters.priceRange[1] < 50000 && l.price > filters.priceRange[1])
        return false;

      // age range
      if (l.wood_age != null) {
        if (l.wood_age < filters.ageRange[0]) return false;
        if (filters.ageRange[1] < 600 && l.wood_age > filters.ageRange[1])
          return false;
      }

      return true;
    });
  }, [listings, filters]);

  const sidebarContent = (
    <MarketplaceFiltersSidebar filters={filters} onChange={setFilters} />
  );

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <div className="container flex-1 py-8">
        <h1 className="mb-1 font-display text-2xl font-bold md:text-3xl">
          Köp &amp; Sälj
        </h1>
        <p className="mb-6 text-sm text-muted-foreground">
          Hitta ved, virke, stockar och enskilda träd
        </p>

        {/* search + mobile filter button */}
        <div className="mb-6 flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Sök annons eller ort…"
              value={filters.search}
              onChange={(e) =>
                setFilters((f) => ({ ...f, search: e.target.value }))
              }
              className="pl-9"
            />
          </div>
          {isMobile && (
            <Button
              variant="outline"
              size="icon"
              onClick={() => setMobileOpen(true)}
            >
              <SlidersHorizontal className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* two-column layout */}
        <div className="flex gap-8">
          {/* desktop sidebar */}
          {!isMobile && (
            <aside className="sticky top-20 hidden w-60 shrink-0 self-start md:block max-h-[calc(100vh-6rem)] overflow-y-auto pr-3">
              {sidebarContent}
            </aside>
          )}

          {/* listings grid */}
          <div className="flex-1">
            {isLoading ? (
              <div className="grid grid-cols-2 gap-3 sm:gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <ListingCardSkeleton key={i} />
                ))}
              </div>
            ) : filtered.length > 0 ? (
              <div className="grid grid-cols-2 gap-3 sm:gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map((l) => (
                  <ListingCard
                    key={l.id}
                    listing={{
                      id: l.id,
                      sellerName: "",
                      category: l.category as any,
                      species: l.species as any,
                      title: l.title,
                      description: l.description ?? "",
                      price: Number(l.price),
                      priceUnit: l.price_unit ?? "",
                      location: l.location ?? "",
                      delivery: l.delivery as any,
                      images: l.images ?? [],
                      status: l.status as any,
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="py-20 text-center text-muted-foreground">
                <p className="text-lg font-medium">Inga annonser hittades</p>
                <p className="text-sm">
                  Prova att ändra din sökning eller filter
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* mobile filter sheet */}
      {isMobile && (
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="bottom" className="h-[90vh] p-0">
            <SheetHeader className="flex flex-row items-center justify-between border-b px-4 py-3">
              <SheetTitle className="text-base">Filter</SheetTitle>
            </SheetHeader>
            <ScrollArea className="h-[calc(90vh-7rem)] px-4 py-4">
              {sidebarContent}
            </ScrollArea>
            <div className="border-t px-4 py-3">
              <Button
                className="w-full"
                onClick={() => setMobileOpen(false)}
              >
                Visa resultat ({filtered.length})
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      )}

      <Footer />
    </div>
  );
}
